import { FC, useState, useCallback, useMemo, useEffect } from "react";
import styled from "@emotion/styled";
import { motion } from "motion/react";
import { FactCardV1SessionPart, ResolvedFactSource, FactSourceKind } from "../session.types";
import { SessionPartProps } from "./session-part.types";
import { MDSText, MDSTextVariant, MDSTextColor, MDSTextWeight } from "@/mds/components/text";
import { MDSIconButton } from "@/mds/components/buttons/icon-button";
import {
  MDSIconButtonVariant,
  MDSIconButtonSize,
} from "@/mds/components/buttons/icon-button/icon-button.types";
import { MDSIcon, MDSIconKind } from "@/mds/components/icon";
import { MDSChip, MDSChipVariant } from "@/mds/components/chips/chip";
import { useIsLastPartInSessionGroup } from "../session-context/mds-session-context.hooks";
import { MemCommonMentionKind } from "@mem-labs/common-editor";
import { MDSTooltip } from "../../tooltip/tooltip";
import { MDSTooltipPlacement } from "../../tooltip/tooltip.types";

type Props = SessionPartProps<FactCardV1SessionPart>;

const COPY_FEEDBACK_DURATION_MS = 1000;

export const MDSSessionFactCardV1Part: FC<Props> = ({ part, config }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [resolvedSource, setResolvedSource] = useState<ResolvedFactSource | null>(null);
  const isLastInGroup = useIsLastPartInSessionGroup({ part });

  // Parse the fact_source string
  const parsedSource = useMemo(() => parseFactSource(part.fact_source), [part.fact_source]);

  // Fetch source label on mount
  useEffect(() => {
    if (!parsedSource || !config.resolveFactSource) return;

    let isMounted = true;

    const fetchSourceLabel = async () => {
      try {
        const resolved = await config.resolveFactSource!(parsedSource.kind, parsedSource.id);
        if (isMounted) {
          setResolvedSource(resolved);
        }
      } catch (error) {
        console.error({
          message: "[MDSSessionFactCardV1Part] Failed to resolve source",
          info: { error: String(error) },
        });
      }
    };

    fetchSourceLabel();

    return () => {
      isMounted = false;
    };
  }, [parsedSource, config.resolveFactSource]);

  const handleCopyValue = useCallback(async () => {
    await copyTextToClipboard({ text: part.fact_value });
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), COPY_FEEDBACK_DURATION_MS);
  }, [part.fact_value]);

  const handleSourceClick = useCallback(
    (event: React.MouseEvent<Element>) => {
      if (!resolvedSource || !config.linkHandler) return;

      event.preventDefault();
      const mentionKind =
        resolvedSource.kind === "NOTE"
          ? MemCommonMentionKind.Note
          : MemCommonMentionKind.Collection;

      config.linkHandler(
        mentionKind,
        resolvedSource.id,
        event.currentTarget as HTMLElement,
        event as React.MouseEvent<HTMLElement>
      );
    },
    [resolvedSource, config]
  );

  const sourceIcon = useMemo(() => {
    if (!resolvedSource) return null;
    return resolvedSource.kind === "NOTE" ? MDSIconKind.FileAlt : MDSIconKind.Collection;
  }, [resolvedSource]);

  return (
    <StyledContainer isLastInGroup={isLastInGroup}>
      <StyledCard>
        <MDSText
          variant={MDSTextVariant.TextXs}
          color={MDSTextColor.Secondary}
          weight={MDSTextWeight.SemiBold}
        >
          {part.fact_label}
        </MDSText>
        <StyledValueRow>
          <MDSText
            variant={MDSTextVariant.HeaderL}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Contrast}
          >
            {part.fact_value}
          </MDSText>
          {isCopied ? (
            <StyledCheckIconWrapper>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                <MDSIcon kind={MDSIconKind.Check} />
              </motion.div>
            </StyledCheckIconWrapper>
          ) : (
            <MDSTooltip
              config={{
                label: "Copy",
                placement: MDSTooltipPlacement.Top,
                delaySeconds: 0.3,
              }}
            >
              <MDSIconButton
                iconKind={MDSIconKind.Copy}
                variant={MDSIconButtonVariant.Default}
                size={MDSIconButtonSize.Small}
                onPress={handleCopyValue}
                aria-label="Copy to clipboard"
              />
            </MDSTooltip>
          )}
        </StyledValueRow>
        {resolvedSource && sourceIcon && (
          <StyledSourceRow>
            <MDSChip icon={sourceIcon} variant={MDSChipVariant.Small} onClick={handleSourceClick}>
              {resolvedSource.label}
            </MDSChip>
          </StyledSourceRow>
        )}
      </StyledCard>
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{ isLastInGroup: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme, isLastInGroup }) =>
    isLastInGroup ? theme.spacing["0.5"] : theme.spacing["2"]};
  margin-left: ${({ theme }) => theme.spacing["7"]};
  margin-top: ${({ theme }) => theme.spacing["2"]};
`;

const StyledCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding: ${({ theme }) => theme.spacing["5"]};
  background-color: ${({ theme }) => theme.colors.surface.preview};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
`;

const StyledValueRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const StyledSourceRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing["1"]};
`;

// Matches MDSIconButton Small size: 8px padding + 14px icon = 30px total
const StyledCheckIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  color: ${({ theme }) => theme.colors.icon.primary};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const copyTextToClipboard = async ({ text }: { text: string }) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    console.error({
      message: "[copyTextToClipboard] Failed.",
      err,
    });
  }
};

/**
 * Parses fact_source string in format "note <uuid>" or "collection <uuid>"
 * Returns null if format is invalid
 */
const parseFactSource = (factSource: string): { kind: FactSourceKind; id: string } | null => {
  if (!factSource) return null;

  const parts = factSource.trim().split(" ");
  if (parts.length !== 2) return null;

  const [kindStr, id] = parts;
  const kindUpper = kindStr.toUpperCase();

  if (kindUpper !== "NOTE" && kindUpper !== "COLLECTION") return null;

  return { kind: kindUpper as FactSourceKind, id };
};
